import React, { useEffect, useRef, useState } from "react";

/**
 * Tarjeta de promoción.
 *
 * `featured` es la tarjeta grande (la de prioridad más baja): muestra la
 * descripción y ocupa toda la altura de la grilla. Las demás van en la columna
 * angosta, con el texto primero y la imagen al costado.
 *
 * Si la foto de `imagen` todavía no existe, cae en la ilustración de `arte`.
 */
const PromoCard = ({ promo, featured = false, spanClassName = "", onOpenConditions }) => {

	const [imageSrc, setImageSrc] = useState(promo.imagen || promo.arte);
	const imageRef = useRef(null);

	const handleImageError = () => {
		if (promo.arte && imageSrc !== promo.arte) setImageSrc(promo.arte);
	};

	// El HTML estático empieza a cargar la imagen antes de hidratar, así que el
	// `onError` puede perderse: al montar revisamos si ya falló.
	useEffect(() => {
		const image = imageRef.current;
		if (image && image.complete && image.naturalWidth === 0) handleImageError();
	}, []);

	const visual = (
		<div className={ `min-h-0 min-w-0 overflow-hidden bg-paper-warm ${featured ? "" : "order-1 lg:order-2"}` }>
			{
				imageSrc &&
					<img
						ref={ imageRef }
						src={ imageSrc }
						onError={ handleImageError }
						alt={ `Promoción de ${promo.marca}` }
						className="h-full w-full object-cover"
					/>
			}
		</div>
	);

	const content = (
		<div className={ `flex min-w-0 flex-col ${featured ? "p-6 sm:p-7 lg:px-[30px] lg:pb-[26px] lg:pt-[31px]" : "order-2 p-6 lg:order-1 lg:px-[22px] lg:pb-[21px] lg:pt-6"}` }>
			<p className="meta">{ promo.marca }</p>

			<h3 className={ `my-3 font-bold leading-[.86] tracking-[-.07em] ${featured ? "text-[54px] lg:text-[68px]" : "text-[44px] lg:text-[48px]"}` }>
				{ promo.titulo }
			</h3>

			{
				featured && promo.descripcion &&
					<p className="m-0 max-w-[320px] text-sm leading-relaxed text-muted">{ promo.descripcion }</p>
			}

			<div className="mt-auto flex items-center justify-between gap-4 border-t border-line pt-4 lg:pt-[18px]">
				<span className="text-[10px] font-semibold uppercase tracking-[.065em] text-muted">
					{ promo.vigencia }
				</span>

				{
					promo.condiciones?.length > 0 &&
						<button
							type="button"
							className="round-link"
							onClick={ () => onOpenConditions(promo) }
							aria-label={ `Ver condiciones de ${promo.marca}` }
						>
							<span aria-hidden="true">→</span>
						</button>
				}
			</div>
		</div>
	);

	if (featured) {
		return (
			<article className={ `relative grid grid-rows-[300px_auto] overflow-hidden bg-white shadow-card lg:grid-cols-[minmax(0,1.42fr)_minmax(260px,.58fr)] lg:grid-rows-none ${spanClassName}` }>
				{ visual }
				{ content }
			</article>
		);
	}

	return (
		<article className="relative grid grid-rows-[250px_auto] overflow-hidden bg-white shadow-card lg:grid-cols-[minmax(0,1fr)_150px] lg:grid-rows-none xl:grid-cols-[minmax(0,1fr)_180px]">
			{ content }
			{ visual }
		</article>
	);
};

export default PromoCard;
